import { AstroAngle, AstroBaseMapKey } from "../../../../models/interfaces";

/** Display order of the planets in every planet list / toggle group. */
export const PLANET_ORDER = [
  "Sun",
  "Moon",
  "Mercury",
  "Venus",
  "Mars",
  "Jupiter",
  "Saturn",
  "Rahu",
  "Ketu",
  "Uranus",
  "Neptune",
  "Pluto",
];

/** Planet the reference implementation pre-selects for the first city search. */
export const DEFAULT_PLANET = "Mars";

export const PLANET_GLYPHS: Record<string, string> = {
  Sun: "☉",
  Moon: "☽",
  Mercury: "☿",
  Venus: "♀",
  Mars: "♂",
  Jupiter: "♃",
  Saturn: "♄",
  Rahu: "☊",
  Ketu: "☋",
  Uranus: "♅",
  Neptune: "♆",
  Pluto: "♇",
};

export const FALLBACK_LINE_COLOR = "#999999";

export const ANGLE_OPTIONS: AstroAngle[] = ["MC", "IC", "ASC", "DSC"];

/** Sentinel value of the "any of the 4 lines" option in the line dropdown. */
export const ANY_ANGLE = "ANY";

/** Aspect-line angles arrive as AC/DC — normalised onto the main-line naming. */
export const ANGLE_NORMALISATION: Record<string, AstroAngle> = {
  AC: "ASC",
  DC: "DSC",
  ASC: "ASC",
  DSC: "DSC",
  MC: "MC",
  IC: "IC",
};

/** Translation key per angle, used for the line dropdown and result rows. */
export const ANGLE_LABEL_KEYS: Record<AstroAngle, string> = {
  MC: "astroAngleMc",
  IC: "astroAngleIc",
  ASC: "astroAngleAsc",
  DSC: "astroAngleDsc",
};

export interface AstroBaseMapDefinition {
  key: AstroBaseMapKey;
  labelKey: string;
  url: string;
  attribution: string;
  subdomains?: string;
  /**
   * Deepest zoom the provider actually serves. Leaflet upscales beyond it
   * instead of showing blank tiles.
   */
  maxNativeZoom?: number;
}

/** Basemap catalogue — mirrors the reference implementation's four options. */
export const BASE_MAPS: AstroBaseMapDefinition[] = [
  {
    key: "colour",
    labelKey: "astroBaseMapColour",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: "&copy; OpenStreetMap",
  },
  // CARTO now stamps "API KEY REQUIRED" over both basemaps.cartocdn.com and
  // its older Fastly host, so the neutral canvases come from Esri instead —
  // free, keyless, and the same provider already serving the satellite layer.
  {
    key: "light",
    labelKey: "astroBaseMapLight",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}",
    attribution: "&copy; Esri, HERE, Garmin, &copy; OpenStreetMap contributors",
    maxNativeZoom: 16,
  },
  {
    key: "dark",
    labelKey: "astroBaseMapDark",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}",
    attribution: "&copy; Esri, HERE, Garmin, &copy; OpenStreetMap contributors",
    maxNativeZoom: 16,
  },
  {
    key: "sat",
    labelKey: "astroBaseMapSatellite",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "&copy; Esri",
  },
];

export const DEFAULT_BASE_MAP: AstroBaseMapKey = "colour";

export const MAP_MAX_ZOOM = 19;
export const MAP_MIN_ZOOM = 2;
export const MAP_DEFAULT_ZOOM = 4;
/** Clamps latitude only — longitude is free so the map wraps endlessly. */
export const MAP_MAX_BOUNDS: [[number, number], [number, number]] = [
  [-85, -1e6],
  [85, 1e6],
];
/** Degrees of longitude in one copy of the world. */
export const WORLD_WIDTH_DEG = 360;
/** Line geometry is drawn in this many copies either side of the centre one. */
export const WORLD_COPY_RANGE = 1;

/** Stroke weight multiplier per zoom level, so lines thicken as you zoom in. */
export const ZOOM_WEIGHT_MIN = 0.62;
export const ZOOM_WEIGHT_MAX = 1.5;
export const ZOOM_WEIGHT_STEP = 0.1;

/**
 * Dark casing drawn beneath each main line to lift it off the basemap. Kept
 * deliberately faint at rest — it is there to separate a line from the terrain
 * behind it, not to make the line itself read as highlighted.
 */
export const LINE_CASING_COLOR = "#05040d";
export const LINE_CASING_EXTRA_WEIGHT = 2;
export const LINE_CASING_REST_OPACITY = 0.2;
export const LINE_CASING_FOCUS_OPACITY = 0.45;

/**
 * At rest the lines sit back so the map stays readable with all of them drawn;
 * the full stroke opacity is reserved for the hovered or opened line.
 */
export const LINE_REST_OPACITY_SCALE = 0.72;

/** Opacity applied to every other line while one is hovered or open. */
export const LINE_DIMMED_OPACITY = 0.16;

/** Approximate label box used for greedy collision avoidance, in pixels. */
export const LABEL_CHAR_WIDTH = 6.2;
export const LABEL_PADDING_X = 30;
export const LABEL_HEIGHT = 22;

/** Used until a profile's real birth place arrives with the line payload. */
export const MAP_FALLBACK_CENTER: [number, number] = [20, 0];

export const RADIUS_MIN_KM = 25;
export const RADIUS_MAX_KM = 500;
export const RADIUS_STEP_KM = 25;
export const RADIUS_DEFAULT_KM = 150;

/** Kilometres per degree of latitude — the equirectangular distance constant. */
export const KM_PER_DEGREE = 111.32;
